import React, { useEffect, useMemo } from "react";
import styles from "./topDonors.module.scss";
import TopDonorCard from "../ui/topDonor/topDonorCard/topDonorCard";
import { observer } from "mobx-react";
import { useStores } from "../../hooks/mobx";
import TopDonorSort from "../ui/topDonor/topDonorSort/topDonorSort";
import Spinner from "../spinner";
import TopDonorFilters from "../ui/topDonor/topDonorFilters/topDonorFilters";
import { useRouter } from "next/router";
import { TFiltersModel } from "./models/FiltersModel";
import { SHARE_LINKS } from "../event/ui/EventMainInfo/EventShareBlock";
import TopDonorsMobileShareModal from "./TopDonorsMobileShareModal/TopDonorsMobileShareModal";
import { Popup } from "semantic-ui-react";
import redirectTo from "../../utils/redirectTo";

const SORT_ITEMS = [
  {
    label: "За всё время",
    value: false,
  },
  {
    label: "За год",
    value: true,
  },
];

type TTopDonors = {
  initialData: TFiltersModel;
};

const TopDonors: React.FC<TTopDonors> = observer(({ initialData }) => {
  const router = useRouter();
  const { authStore, tokenStore } = useStores();
  const { user } = authStore;

  const { topDonorPageStore } = useStores();
  const { isContentLoading, users, model, onChangeMain, sortQueryString } =
    topDonorPageStore;

  useEffect(() => {
    if (!initialData) {
      topDonorPageStore.initStore();
    } else {
      topDonorPageStore.initWithInitialData(initialData);
    }
  }, [tokenStore]);

  useEffect(() => {
    if (sortQueryString) {
      const newAsPath = `/top/${sortQueryString}`;
      router.replace(router.pathname, newAsPath);
    } else {
      const newAsPath = `/top/`;
      router.replace(router.pathname, newAsPath);
    }
  }, [sortQueryString, model.by_year]);

  const onChangeSort = async (value) => {
    onChangeMain("by_year", value);
    await topDonorPageStore.getData();
  };

  return (
    <div className={styles.TopDonors}>
      <TopDonorsHeader />
      <TopDonorsMobileShareModal />
      <div className={styles.TopDonors_Container}>
        <div className={styles.TopDonors_Container_Content}>
          <div className={styles.TopDonors_Container_Content_Data}>
            <TopDonorsMobileFixedContent />
            <TopDonorSort
              items={SORT_ITEMS}
              onSelect={(value) => onChangeSort(value)}
              selected={model["by_year"]}
            />
            {!isContentLoading &&
              !!users.length &&
              users.map((item, index) => (
                <TopDonorCard
                  user={item}
                  place={index + 1}
                  key={item.id}
                  isMe={item.id === user?.id}
                />
              ))}
            {!isContentLoading && !users.length && (
              <TopDonorsNullStateComponent />
            )}
            {isContentLoading && (
              <div className={styles.TopDonors_Container_Content_Data_Spinner}>
                <Spinner toggled={true} />
              </div>
            )}
          </div>
          <TopDonorFilters />
        </div>
      </div>
    </div>
  );
});

const TopDonorsMobileFixedContent: React.FC = observer(() => {
  return (
    <div className={styles.TopDonorsMobileFixedContent}>
      <TopDonorsMobileFixedContentShare />
      <TopDonorsMobileFixedContentFilters />
    </div>
  );
});

const TopDonorsMobileFixedContentShare: React.FC = observer(() => {
  const { topDonorPageStore } = useStores();

  const onClick = () => {
    topDonorPageStore.setShareOpenModal(true);
  };

  return (
    <div
      onClick={onClick}
      className={styles.TopDonorsMobileFixedContentContainer}
      data-side={"left"}
    >
      <div className={styles.TopDonorsMobileFixedContentShare}>
        <img src="/static/images/top/share2.svg" alt="share button" />
      </div>
    </div>
  );
});

const TopDonorsMobileFixedContentFilters: React.FC = observer(() => {
  const { topDonorPageStore } = useStores();
  const { appliedFiltersCount } = topDonorPageStore;

  const onClick = () => {
    topDonorPageStore.setOpenModal(true);
  };

  return (
    <div
      onClick={onClick}
      className={styles.TopDonorsMobileFixedContentContainer}
      data-side={"right"}
    >
      <div className={styles.TopDonorsMobileFixedContentFilters}>
        {appliedFiltersCount > 0 && (
          <div className={styles.TopDonorsMobileFixedContentFilters_Count}>
            <div>{appliedFiltersCount}</div>
          </div>
        )}
        <div className={styles.TopDonorsMobileFixedContentFilters_Image}>
          <img src="/static/images/top/Sliders.svg" alt="filters button" />
        </div>
      </div>
    </div>
  );
});

const TopDonorsNullStateComponent: React.FC = () => {
  const { topDonorPageStore } = useStores();

  const onClickClear = () => {
    topDonorPageStore.resetFilters();
  };

  return (
    <div className={styles.TopDonorsNullStateComponent}>
      <div className={styles.TopDonorsNullStateComponent_Image}>
        <img src="/static/images/top/nu_bivaet2.png" alt="not found" />
      </div>
      <div className={styles.TopDonorsNullStateComponent_Text}>
        <span>По выбранным параметрам никого нет</span>
      </div>
      <div className={styles.TopDonorsNullStateComponent_Action}>
        <div onClick={onClickClear}>Очистить фильтры</div>
      </div>
    </div>
  );
};

const TopDonorsHeader: React.FC = () => {
  return (
    <div className={styles.TopDonorsHeader}>
      <div className={styles.TopDonorsHeader_Container}>
        <TopDonorsHeaderInfo />
        <TopDonorsHeaderShare />
      </div>
    </div>
  );
};

const TopDonorsHeaderInfo: React.FC = () => {
  return (
    <>
      <div className={styles.TopDonorsHeaderInfo}>
        <div className={styles.TopDonorsHeaderInfo_Image}>
          <img src="/static/images/top/sport.png" alt="sport blood" />
        </div>
        <div className={styles.TopDonorsHeaderInfo_Text}>
          <div className={styles.TopDonorsHeaderInfo_Text_Title}>
            <h1>Топ 100 доноров</h1>
          </div>
          <div className={styles.TopDonorsHeaderInfo_Text_Lead}>
            <span>
              Вдохновляйтесь достижениями других доноров и отслеживайте свои по
              выбранным фильтрам!
            </span>
          </div>
        </div>
      </div>
      <div className={styles.TopDonorsHeaderInfoLeadMobile}>
        <span>
          Вдохновляйтесь достижениями других доноров и отслеживайте свои по
          выбранным фильтрам!
        </span>
      </div>
    </>
  );
};

const TopDonorsHeaderShare: React.FC = observer(() => {
  return (
    <div className={styles.TopDonorsHeaderShare}>
      <TopDonorsHeaderSharePlace />
      <TopDonorsHeaderShareLinks />
    </div>
  );
});

const TopDonorsHeaderSharePlace: React.FC = observer(() => {
  const { topDonorPageStore, authStore } = useStores();
  const { userData, pseudoModel } = topDonorPageStore;

  const isUserInTop = useMemo(() => {
    return typeof userData?.place === "number";
  }, [userData, authStore?.user]);

  const userPlace = useMemo(() => {
    if (isUserInTop) return userData.place;
    return "?";
  }, [userData, authStore?.user]);

  const actionText = useMemo(() => {
    if (authStore?.user) {
      return "Добавить донацию";
    } else {
      return "Войти";
    }
  }, [authStore?.user, userData]);

  const actionImage = useMemo(() => {
    if (authStore?.user) {
      return "/static/images/top/with-text.svg";
    } else {
      return "/static/images/top/login_btn.svg";
    }
  }, [authStore?.user, userData]);

  const onActionClick = () => {
    if (isUserInTop) return;
    if (authStore?.user) {
      redirectTo("/profile/add_donation/");
    } else {
      redirectTo("/login/?next=/top");
    }
  };

  return (
    <div className={styles.TopDonorsHeaderSharePlace}>
      <div className={styles.TopDonorsHeaderSharePlace_Title}>
        <span>Ваше место</span>
      </div>
      <div className={styles.TopDonorsHeaderSharePlace_Content}>
        <div className={styles.TopDonorsHeaderSharePlace_Content_Place}>
          <Popup
            inverted
            basic
            disabled={isUserInTop || !authStore?.user}
            style={{ backgroundColor: "#333333", zIndex: 10000 }}
            hideOnScroll={true}
            position={"top center"}
            trigger={<div>{userPlace}</div>}
          >
            {pseudoModel["city"]?.label === authStore?.user?.city?.title
              ? "Делайте больше донаций, чтобы попасть в топ"
              : "Вы не участвуете в рейтинге, т.к. город, указанный в профиле, отличается от города, выбранного в фильтре"}
          </Popup>
        </div>
        <div className={styles.TopDonorsHeaderSharePlace_Content_Text}>
          <span>Для выбранных фильтров</span>
        </div>
      </div>
      {!isUserInTop && (
        <div className={styles.TopDonorsHeaderSharePlace_Message}>
          <span onClick={onActionClick}>{actionText}</span>
          <img onClick={onActionClick} src={actionImage} alt="login" />
        </div>
      )}
    </div>
  );
});

const TopDonorsHeaderShareLinks: React.FC = observer(() => {
  const router = useRouter();
  const { topDonorPageStore } = useStores();
  const { userData, appliedFiltersCount } = topDonorPageStore;

  const content = useMemo(() => {
    if (typeof userData?.place === "number") {
      if (userData?.place <= 100) {
        return `Я занимаю ${userData.place} место в топе${
          appliedFiltersCount > 0 ? " по выбранным фильтрам" : ""
        }`;
      }
    }
    return "Топ 100 доноров";
  }, [userData, appliedFiltersCount]);

  const currPath = useMemo(() => {
    if (typeof window !== "undefined") {
      return `${window.location.origin}${router.asPath}`;
    } else {
      return `https://donorsearch.org${router.asPath}`;
    }
  }, [router]);

  return (
    <div className={styles.TopDonorsHeaderShareLinks}>
      <div className={styles.TopDonorsHeaderShareLinks_Text}>
        <span>Поделиться</span>
      </div>
      <div className={styles.TopDonorsHeaderShareLinks_Links}>
        {SHARE_LINKS.map((item) =>
          item.component(currPath, content, `key_${item.key}`, " ")
        )}
      </div>
    </div>
  );
});

export default TopDonors;
